import { useState } from "react";
import Dropzone from "react-dropzone";
import CustomInput from "../atoms/CustomInput";
import CustomSelect from "../atoms/CustomSelect";
import CustomTextArea from "../atoms/CustomTextarea";
import { AlertSuccess, AlertError } from "../atoms/Alerts";
import { countries, studyLevels } from "../atoms/SelectsInformationJson";
import { SubmitFormButton } from "../atoms/Buttons";
import ConsentCheckbox from "../atoms/ConsentCheckbox";
import { UpploadFileIcon } from "../atoms/Icons";

export default function SummerCampForm({ setOpen, translations }) {
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [city, setCity] = useState("");
  const [selectedStudyLevel, setSelectedStudyLevel] = useState("");
  const [studyArea, setStudyArea] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [inputErrors, setInputErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const MESSAGES = {
    required: translations?.required_field,
    invalidEmail: translations?.insert_email,
    fileRequired: translations?.add_file,
    submitSuccess: translations?.application_success,
    invalidPhoneNumber: props._context?.translations?.invalid_number
  };

  const isValidEmail = (email) => {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const errors = {};

    if (!name) errors.name = MESSAGES.required;
    if (!isValidEmail(email)) errors.email = MESSAGES.invalidEmail;
    if (!phoneNumber) {
      errors.phoneNumber = MESSAGES.required;
    } else if (!/^\d+$/.test(phoneNumber)) {
      errors.phoneNumber = MESSAGES.invalidPhoneNumber;
    }
    if (!selectedCountry) errors.selectedCountry = MESSAGES.required;
    if (!city) errors.city = MESSAGES.required;
    if (!selectedStudyLevel) errors.studyLevel = MESSAGES.required;
    if (!studyArea) errors.studyArea = MESSAGES.required;
    if (!message) errors.message = MESSAGES.required;
    if (files.length < 1) errors.files = MESSAGES.fileRequired;
    if (!consent) errors.consent = MESSAGES.required;

    return errors;
  };

  const clearForm = () => {
    setName("");
    setEmail("");
    setPhoneNumber("");
    setSelectedCountry("");
    setCity("");
    setSelectedStudyLevel("");
    setStudyArea("");
    setMessage("");
    setFiles([]);
    setInputErrors({});
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setInputErrors({});
    setSuccess(false);
    setSubmitting(true);

    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setInputErrors(formErrors);
    } else {
      try {
        // First, send the form data without files
        const summercampApplicationData = {
          name,
          email,
          phone_number: phoneNumber,
          country: selectedCountry,
          city,
          study_level: translations?.[selectedStudyLevel] || selectedStudyLevel,
          area_of_study: studyArea,
          message,
        };

        const response = await fetch("/api/summercamp-applications", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ data: summercampApplicationData }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error("Error details:", errorData);
          setInputErrors({
            submit: translations?.failed_to_submit,
          });
          return;
        }

        const result = await response.json();
        const applicationId = result.data.id;

        // If files are present, upload them separately
        if (files && files.length > 0) {
          const formData = new FormData();

          // Add each file to the form data
          for (const file of files) {
            formData.append("files", file);
          }

          // Add Strapi's reference information
          formData.append("refId", applicationId);
          formData.append(
            "ref",
            "api::summercamp-application.summercamp-application"
          );
          formData.append("field", "file"); // Name of the media field in your content type

          const uploadResponse = await fetch("/api/upload", {
            method: "POST",
            body: formData,
          });

          if (!uploadResponse.ok) {
            console.error("File upload failed:", await uploadResponse.json());
            setInputErrors({ submit: "File upload failed. Please try again." });
            return;
          }
        }

        // Clear the form and show a success message
        clearForm();
        setSuccess(true);
        setSuccessMessage(MESSAGES.submitSuccess);
        setSubmitting(false);
      } catch (error) {
        console.error("Error submitting summer camp application:", error);
        setInputErrors({
          submit: translations?.failed_to_submit,
        });
        setSubmitting(false);
      }
    }
  };

  return (
    <>
      <AlertSuccess show={success} msg={successMessage} />
      <AlertError show={false} msg="" />
      <form>
        <div className="grid grid-cols-12 gap-16 640:gap-20 1024:gap-24">
          <div className="col-span-12">
            <CustomInput
              label="Name"
              name="name"
              id="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={inputErrors.name}
              bgColor="bg-gray-200"
            />
          </div>
          <div className="col-span-12 640:col-span-6">
            <CustomInput
              label="Email"
              name="email"
              id="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={inputErrors.email}
              bgColor="bg-gray-200"
            />
          </div>
          <div className="col-span-12 640:col-span-6">
            <CustomInput
              label="Phone number"
              name="phone-number"
              id="phone-number"
              required
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              error={inputErrors.phoneNumber}
              bgColor="bg-gray-200"
            />
          </div>
          <div className="col-span-12 640:col-span-6">
            <CustomSelect
              options={countries.en}
              value={selectedCountry}
              onChange={(value) => setSelectedCountry(value)}
              placeholder="Country"
              required
              error={inputErrors.selectedCountry}
              bgColor="bg-gray-200"
            />
          </div>
          <div className="col-span-12 640:col-span-6">
            <CustomInput
              label="City"
              name="city"
              id="city"
              required
              value={city}
              onChange={(e) => setCity(e.target.value)}
              error={inputErrors.city}
              bgColor="bg-gray-200"
            />
          </div>
          <div className="col-span-12 640:col-span-6">
            <CustomSelect
              translations={translations}
              options={studyLevels}
              value={selectedStudyLevel}
              onChange={(value) => setSelectedStudyLevel(value)}
              placeholder="Study Level"
              required
              error={inputErrors.studyLevel}
              bgColor="bg-gray-200"
            />
          </div>
          <div className="col-span-12 640:col-span-6">
            <CustomInput
              label="Area of Study"
              name="study-area"
              id="study-area"
              required
              value={studyArea}
              onChange={(e) => setStudyArea(e.target.value)}
              error={inputErrors.studyArea}
              bgColor="bg-gray-200"
            />
          </div>
          <div className="col-span-12">
            <CustomTextArea
              label="Message"
              name="message"
              id="message"
              value={message}
              required
              onChange={(e) => {
                setMessage(e.target.value);
              }}
              error={inputErrors.message}
              bgColor="bg-gray-200"
            />
          </div>
          <div className="col-span-12">
            <Dropzone
              accept={{ "application/pdf": [".pdf"] }}
              onDrop={(acceptedFiles) => setFiles(acceptedFiles)}
              multiple={false}
            >
              {({ getRootProps, getInputProps }) => (
                <section className="relative py-10 px-12 bg-transparent border border-gray-500 rounded-xxxs w-full min-h-[140px] cursor-pointer">
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    {files.length == 0 ? (
                      <div>
                        <div>
                          <span className="text-xxs font-medium leading-115 tracking-05 text-gray-700">
                            Upload file
                            <span className="text-red-500"> *</span>
                          </span>
                        </div>
                        <div className="absolute inset-0">
                          <div className="absolute bottom-18 left-12">
                            <UpploadFileIcon />
                            <p className="text-xxxs text-gray-700">
                              Drag and drop a PDF File
                            </p>
                            <p className="text-xxxs text-gray-700">
                              Maximum 15Mb
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="absolute inset-0">
                            <div className="absolute bottom-18 left-12">
                              <UpploadFileIcon />
                              <p className="text-xxxs text-gray-700">
                                Added file
                              </p>
                              <p className="text-xxxs text-gray-700">
                                {files[0].name}
                              </p>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </section>
              )}
            </Dropzone>
            {inputErrors.files && (
              <span className="block small text-red-500 mt-4 duration-250">
                {inputErrors.files}
              </span>
            )}
          </div>
          <div className="col-span-12 flex flex-col">
            <ul className="list-disc pl-15 text-gray-700">
              <li>
                <span className="text-xxxs tracking-05">
                  The fields marked with * are mandatory
                </span>
              </li>
              <li>
                <span className="text-xxxs tracking-05">
                  You can only apply once
                </span>
              </li>
            </ul>
          </div>
          <div className="col-span-12">
            <ConsentCheckbox
              label={translations?.confirm_conditions}
              terms_and_conditions={translations?.terms_and_conditions}
              privacy_policy_url={translations?.privacy_policy_url}
              name="consent"
              id="consent"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              setOpen={setOpen}
              error={inputErrors.consent}
            />
          </div>
          <div className="col-span-12">
            <SubmitFormButton disabled={submitting} text="Submit Application" onClick={submitForm} />
          </div>
        </div>
      </form>
    </>
  );
}
